import React, { useEffect } from "react";
import {
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { LoanEvent, LoanEventType, FeeType } from "./types";

type Props = {
  open: boolean;
  loading?: boolean;
  event?: LoanEvent | null;
  installmentsCount?: number;
  onCancel: () => void;
  onSubmit: (values: any) => void;
};

const LoanEventModal: React.FC<Props> = ({
  open,
  loading,
  event,
  installmentsCount = 12,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  useEffect(() => {
    if (event) {
      form.setFieldsValue({
        ...event,
        eventDate: event.eventDate ? dayjs(event.eventDate) : undefined,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        eventType: "FEE" as LoanEventType,
        eventDate: dayjs(),
        feeType: "CAPITALIZED" as FeeType, // Default: fee se dodaje na dug (češće kod bankarskih kredita)
      });
    }
  }, [event, form]);

  const eventType = Form.useWatch("eventType", form) as
    | LoanEventType
    | undefined;
  const feeType = Form.useWatch("feeType", form) as FeeType | undefined;

  return (
    <Modal
      open={open}
      title={event ? t("Update loan event") : t("Add loan event")}
      onCancel={onCancel}
      className="standardHeader standardFooter"
      onOk={() => form.submit()}
      okButtonProps={{ loading }}
      destroyOnClose
    >
      <Form layout="vertical" form={form} onFinish={onSubmit}>
        <Form.Item
          name="eventType"
          label={t("Event type")}
          rules={[{ required: true, message: t("Select event type") }]}
        >
          <Select>
            <Select.Option value="FEE">{t("Fee")}</Select.Option>
            <Select.Option value="PREPAYMENT">{t("Prepayment")}</Select.Option>
            <Select.Option value="GRACE_INTEREST">
              {t("Grace interest")}
            </Select.Option>
            <Select.Option value="OTHER_DISBURSEMENT">
              {t("Other disbursement")}
            </Select.Option>
            <Select.Option value="OTHER_PAYMENT">
              {t("Other payment")}
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="eventDate"
          label={t("Event date")}
          rules={[{ required: true, message: t("Select event date") }]}
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          name="amount"
          label={t("Amount")}
          rules={[
            { required: true, message: t("Amount is required") },
            { type: "number", min: 0.01 },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            step={100}
            placeholder="0.00"
          />
        </Form.Item>

        <Form.Item name="description" label={t("Description")}>
          <Input.TextArea rows={3} placeholder={t("Optional note")} />
        </Form.Item>

        {eventType === "FEE" && (
          <>
            <Form.Item
              name="feeType"
              label={t("Fee type")}
              rules={[{ required: true }]}
              tooltip={t(
                "CHARGED: Fee is deducted from disbursement (reduces net payout). CAPITALIZED: Fee is added to principal (increases total debt and is repaid through installments).",
              )}
            >
              <Radio.Group>
                <Radio.Button value="CHARGED">
                  {t("Charged (deducted from payout)")}
                </Radio.Button>
                <Radio.Button value="CAPITALIZED">
                  {t("Capitalized (added to debt)")}
                </Radio.Button>
              </Radio.Group>
            </Form.Item>

            {feeType === "CHARGED" && (
              <Form.Item
                name="feeChargedAt"
                label={t("Charged at period")}
                rules={[
                  { required: true, message: t("Period number is required") },
                  { type: "number", min: 1, max: installmentsCount },
                ]}
                tooltip={t("The period number where this fee will be added")}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={1}
                  max={installmentsCount}
                  step={1}
                  placeholder="1"
                />
              </Form.Item>
            )}
          </>
        )}
      </Form>
    </Modal>
  );
};

export default React.memo(LoanEventModal);
